import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, authHelpers } from '../config/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // 获取用户资料
  const fetchUserProfile = async (currentSession) => {
    if (!currentSession?.access_token) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`
        }
      });

      if (response.status === 401) {

        // 清除 React 状态
        setUser(null);
        setUserProfile(null);
        setSession(null);

        // 清除 Supabase 本地存储（不发送网络请求）
        try {
          localStorage.removeItem('supabase.auth.token');
          // 清除所有 supabase 相关的 localStorage
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
        } catch (e) {
          // ignore
        }

        return;
      }

      if (response.ok) {
        const result = await response.json();
        setUserProfile(result.data);
      }
    } catch (error) {
      // network error
    }
  };

  // 初始化：检查当前会话
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          await fetchUserProfile(currentSession);
        }
      } catch (error) {
        // init error
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          await fetchUserProfile(currentSession);
        } else {
          setUserProfile(null);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // 注册
  const signUp = async (email, password) => {
    const { data, error } = await authHelpers.signUp(email, password);
    if (error) throw error;
    return data;
  };

  // 登录
  const signIn = async (email, password) => {
    const { data, error } = await authHelpers.signIn(email, password);
    if (error) throw error;

    // 登录成功后获取用户资料
    if (data.session) {
      setSession(data.session);
      await fetchUserProfile(data.session);
    }

    return data;
  };

  // 登出
  const signOut = async () => {
    try {
      // 尝试调用 Supabase 的 signOut API
      await authHelpers.signOut();
    } catch (error) {
      // signOut API failed, still clearing local state
    }

    // 无论 API 调用是否成功，都清除本地状态
    setUser(null);
    setUserProfile(null);
    setSession(null);

    // 清除本地存储中的会话信息
    try {
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
    } catch (e) {
      // ignore
    }
  };

  // 刷新用户资料
  const refreshProfile = async () => {
    if (session) {
      await fetchUserProfile(session);
    }
  };

  // 重置密码
  const resetPassword = async (email) => {
    const { data, error } = await authHelpers.resetPassword(email);
    if (error) throw error;
    return data;
  };

  // 更新密码
  const updatePassword = async (newPassword) => {
    const { data, error } = await authHelpers.updatePassword(newPassword);
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    userProfile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
